(() => {
  'use strict';

  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopMotion = window.matchMedia('(min-width: 761px) and (prefers-reduced-motion: no-preference)');
  const headlineSelector = '#MainContent h1, #MainContent h2, #MainContent h3';
  const imageSelector = [
    '.v5-catalogue__hero-art',
    '.v5-catalogue__piece .v5-catalogue__photo',
    '.bh-pdp__photo',
    '.v9-chamber__media',
    '.v13-gallery__viewer',
    '.v10-invitation__photograph',
    '.v4-atlas__photo'
  ].join(',');

  let headingObserver;
  let imageObserver;
  const activeImages = new Set();
  let shift = 0;
  let velocity = 0;
  let impulse = 0;
  let frame = 0;
  let priorScroll = window.scrollY;

  const visibleImmediately = element => {
    const rect = element.getBoundingClientRect();
    return rect.height < 1 || rect.top < window.innerHeight * .82 || rect.bottom < 0;
  };

  const registerHeadlines = (scope = document) => {
    if (reducedMotion.matches) return;
    if (!headingObserver && 'IntersectionObserver' in window) {
      headingObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('bk6-inview');
          headingObserver.unobserve(entry.target);
        }
      }, { threshold: .06, rootMargin: '0px 0px -7% 0px' });
    }
    let sequence = 0;
    for (const element of scope.querySelectorAll(headlineSelector)) {
      if (element.dataset.bk6Ready === 'true') continue;
      if (element.closest('.v5-nav, .v8-opening, [hidden], details:not([open]), [aria-hidden="true"], .v5-lightbox, .v5-catalogue__piece')) continue;
      if (element.closest('[data-reveal], [data-bt5-reveal], [data-v5-reveal], [data-v10-reveal], [data-v13-reveal], .v4-reveal, .v8-editorial')) continue;
      if (element.getClientRects().length === 0) continue;
      element.dataset.bk6Ready = 'true';
      element.dataset.bk6Headline = '';
      element.style.setProperty('--bk6-headline-delay', `${(sequence % 3) * 95}ms`);
      sequence++;
      if (!headingObserver || visibleImmediately(element)) element.classList.add('bk6-inview');
      else headingObserver.observe(element);
    }
  };

  const paintImages = () => {
    if (Math.abs(shift) < .05 && Math.abs(velocity) < .05 && Math.abs(impulse) < .05) {
      frame = 0;
      shift = 0;
      velocity = 0;
      impulse = 0;
      for (const element of activeImages) element.style.removeProperty('--bk6-image-drift');
      return;
    }
    velocity = (velocity + (impulse - shift) * .052) * .75;
    shift += velocity;
    impulse *= .86;
    for (const element of activeImages) {
      if (!element.isConnected) { activeImages.delete(element); continue; }
      const weight = element.classList.contains('v5-catalogue__photo') ? .68 : 1;
      element.style.setProperty('--bk6-image-drift', `${(shift * weight).toFixed(2)}px`);
    }
    frame = window.requestAnimationFrame(paintImages);
  };

  const onScroll = () => {
    const scroll = window.scrollY;
    const delta = scroll - priorScroll;
    priorScroll = scroll;
    if (!desktopMotion.matches || document.hidden || activeImages.size === 0) return;
    impulse = Math.max(-11, Math.min(11, impulse - delta * .092));
    if (!frame) frame = window.requestAnimationFrame(paintImages);
  };

  const registerImages = (scope = document) => {
    if (!desktopMotion.matches || !('IntersectionObserver' in window)) return;
    if (!imageObserver) {
      imageObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) activeImages.add(entry.target);
          else {
            activeImages.delete(entry.target);
            entry.target.style.removeProperty('--bk6-image-drift');
          }
        }
      }, { rootMargin: '90px 0px 90px 0px', threshold: 0 });
    }
    for (const element of scope.querySelectorAll(imageSelector)) {
      if (element.dataset.bk6InertiaReady) continue;
      element.dataset.bk6InertiaReady = 'true';
      element.classList.add('bk6-inertial');
      imageObserver.observe(element);
    }
  };

  const resetInertia = () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    velocity = shift = impulse = 0;
    priorScroll = window.scrollY;
    for (const element of activeImages) element.style.removeProperty('--bk6-image-drift');
  };

  const updateMotion = () => {
    root.classList.toggle('bk6-ready', !reducedMotion.matches);
    if (reducedMotion.matches) {
      resetInertia();
      return;
    }
    registerHeadlines(document);
    if (desktopMotion.matches) registerImages(document);
    else resetInertia();
  };

  const start = () => {
    updateMotion();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pageshow', () => { priorScroll = window.scrollY; updateMotion(); });
    document.addEventListener('visibilitychange', () => { if (document.hidden) resetInertia(); });
    document.addEventListener('shopify:section:load', event => {
      if (reducedMotion.matches) return;
      registerHeadlines(event.target);
      registerImages(event.target);
    });
    reducedMotion.addEventListener('change', updateMotion);
    desktopMotion.addEventListener('change', updateMotion);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
